import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Ban } from "lucide-react";
import { useAuth } from "../../../../../contexts/AuthContext";
import { useDashTheme } from "../../../hooks/useDashTheme";
import {
  useClassicFilters, classicPath, subjectPath, alertSel,
} from "../../../components/drilldown/drilldownRoutes";
import { orgAlertsGet, orgAlertsStream } from "../../../components/drilldown/drilldownApi";
import { DrillFrame } from "./DrillFrame";
import useEscapeBack from "./useEscapeBack";
import { AlertRows, AlertDetail, AlertTabs, paintAlert } from "../Panels";
import { inks, paper, prettyDay } from "../viz";

export default function GraphicalAlerts() {
  const t = useDashTheme();
  const k = inks(t);
  const ppr = paper(t);
  const navigate = useNavigate();
  const [params, setSearchParams] = useSearchParams();
  const { range, member } = useClassicFilters();
  const { user } = useAuth();
  const isLead = Boolean(user && (user.role === "org_admin" || user.role === "main_admin"));
  const tab = params.get("tab") === "work" ? "work" : "blocker";
  const sel = params.get("sel") || "";
  const backTo = classicPath(range, member);

  useEscapeBack(React.useCallback(() => navigate(backTo), [navigate, backTo]));

  React.useEffect(() => {
    if (user && !isLead) navigate(backTo, { replace: true });
  }, [user, isLead, navigate, backTo]);

  const [rows, setRows] = React.useState([]);
  const [lede, setLede] = React.useState("");
  const [empty, setEmpty] = React.useState("");
  const [writing, setWriting] = React.useState(true);

  React.useEffect(() => {
    if (!isLead) return undefined;
    const ac = new AbortController();
    let alive = true;
    setRows([]);
    setLede("");
    setEmpty("");
    setWriting(true);
    const take = (list, summary) => {
      if (!alive) return;
      setRows((list || []).map(paintAlert));
      if (summary) setLede(summary);
    };
    (async () => {
      try {
        const pack = await orgAlertsGet(range, { member, signal: ac.signal });
        const cached = (pack?.alerts || []).filter((a) => a && a.title);
        if (cached.length) {
          take(cached, pack.summary);
          setWriting(false);
          return;
        }
        orgAlertsStream(range, { member, signal: ac.signal }, (d) => {
          if (d.type === "lede") setLede(d.text || "");
          else if (d.type === "alert") setRows((cur) => [...cur, paintAlert(d)]);
          else if (d.type === "empty" || d.type === "error") setEmpty(d.message || "");
          else if (d.type === "done") setWriting(false);
        }).catch((e) => {
          if (e.name !== "AbortError" && !ac.signal.aborted) setEmpty(e.message || "Could not write alerts.");
        }).finally(() => { if (!ac.signal.aborted) setWriting(false); });
      } catch (e) {
        if (e.name !== "AbortError" && !ac.signal.aborted) setEmpty(e.message || "Could not read alerts.");
        if (!ac.signal.aborted) setWriting(false);
      }
    })();
    return () => { alive = false; ac.abort(); };
  }, [isLead, range, member]);

  const blockers = rows.filter((r) => r.kind === "blocker");
  const alerts = rows.filter((r) => r.kind !== "blocker");
  const shown = tab === "blocker" ? blockers : alerts;
  const open = shown.find((r) => alertSel(r) === sel) || shown[0] || null;

  const writeParams = (nextTab, row) => {
    const p = new URLSearchParams(params);
    p.set("tab", nextTab);
    if (row) p.set("sel", alertSel(row));
    else p.delete("sel");
    setSearchParams(p, { replace: true });
  };

  const start = params.get("start");
  const end = params.get("end");
  const when = start && end && start === end
    ? prettyDay(start)
    : start && end ? `${prettyDay(start)} – ${prettyDay(end)}` : "";

  return (
    <DrillFrame
      t={t}
      title="Needs attention"
      icon={<AlertTriangle size={22} strokeWidth={2.2} />}
      tone="gold"
      eyebrow="Dashboard · needs attention"
      subject={[]}
      backTo={backTo}
      crumbs={[{ label: "Dashboard", to: backTo }, { label: "Needs attention" }]}
      sentence={writing && !rows.length ? "Reading the work…" : (
        <>
          {when ? <>On <b>{when}</b>. </> : null}
          {blockers.length ? <><b>{blockers.length}</b> blocker{blockers.length === 1 ? "" : "s"}</> : "No blockers"}
          {alerts.length ? <> · <b>{alerts.length}</b> alert{alerts.length === 1 ? "" : "s"}</> : null}
          {lede ? `. ${lede}` : "."}
        </>
      )}
      readings={rows.length ? [
        { icon: <Ban size={16} strokeWidth={2.2} />, value: String(blockers.length), label: "blockers", tone: "gold" },
        { icon: <AlertTriangle size={16} strokeWidth={2.2} />, value: String(alerts.length), label: "alerts", tone: "blue" },
        open ? { value: open.kind === "blocker" ? "Blocker" : "Alert", label: open.names || "selected", tone: open.kind === "blocker" ? "gold" : "blue" } : null,
      ].filter(Boolean) : []}
    >
      <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(240px, 320px) minmax(0, 1fr)",
        gap: 12, alignItems: "stretch", minHeight: 420,
      }} className="tgw-alert-split">
        <div style={{
          background: ppr.card, border: `1px solid ${t.c.border}`, borderRadius: t.r.card,
          padding: "14px 12px 14px", minWidth: 0, display: "flex", flexDirection: "column",
        }}>
          <AlertTabs
            t={t} tab={tab}
            onTab={(id) => writeParams(id, (id === "blocker" ? blockers : alerts)[0])}
            blockers={blockers.length} alerts={alerts.length}
          />
          <div className="tgw-alerts-scroll" style={{ flex: 1, minHeight: 0 }}>
            {writing && !shown.length ? (
              <p style={{ margin: 0, fontSize: 13, color: t.c.textSecondary }}>Reading the work…</p>
            ) : !shown.length ? (
              <p style={{ margin: 0, fontSize: 13, color: t.c.textSecondary }}>
                {empty || (tab === "blocker" ? "No named blocker yet." : "No named alert yet.")}
              </p>
            ) : (
              <AlertRows
                t={t} k={k} ppr={ppr} rows={shown} selected={open} rail
                onOpen={(row) => writeParams(tab, row)}
              />
            )}
          </div>
        </div>
        <div style={{
          background: ppr.card, border: `1px solid ${t.c.border}`, borderRadius: t.r.card,
          padding: 18, minWidth: 0,
        }}>
          {open ? (
            <AlertDetail
              t={t} k={k} ppr={ppr} row={open}
              onDrill={(drill) => navigate(subjectPath(drill, range, member))}
            />
          ) : (
            <p style={{ margin: 0, fontSize: 13.5, color: t.c.textSecondary }}>
              Pick one on the left.
            </p>
          )}
        </div>
      </div>
    </DrillFrame>
  );
}
